/**
 * The `html` page type's registry declaration: an interactive HTML page,
 * rendered through the sandboxed HTML embed with a toggle to its source.
 *
 * A hand-written manifest for the reason `builtinMonacoExtension.ts` has one:
 * the editor ships with the runtime, not as an extension package. The id is
 * the one the desktop records as `editorId` on the pages it shares
 * (`builtin.html`), and the document type is the codec's (`html`), so a page
 * shared from the desktop resolves here.
 *
 * `.html` is also among Monaco's code suffixes. A shared page is matched on its
 * document type first, so an `html` page opens here and a `code` page that
 * happens to be named `.html` still opens as code. Like Monaco, it is not in
 * `newFileMenu`: nothing in the browser seeds a new room yet.
 */

import { registerBrowserExtension, type BrowserExtensionManifest } from '@nimbalyst/web-editors/registry';

export const BUILTIN_HTML_PAGE_EXTENSION_ID = 'builtin.html';
export const BUILTIN_HTML_PAGE_COMPONENT = 'HtmlPageEditor';

export const BUILTIN_HTML_PAGE_MANIFEST: BrowserExtensionManifest = {
  id: BUILTIN_HTML_PAGE_EXTENSION_ID,
  name: 'HTML page',
  contributions: {
    customEditors: [
      {
        filePatterns: ['*.html'],
        displayName: 'HTML page',
        component: BUILTIN_HTML_PAGE_COMPONENT,
        collaboration: { supported: true, documentType: 'html' },
      },
    ],
    fileIcons: { '*.html': 'html' },
  },
  marketplace: { icon: 'html' },
};

/** Declares the manifest only; the page's bundle loads the first time one opens. */
export function registerBuiltinHtmlPageExtension(): void {
  registerBrowserExtension({
    manifest: BUILTIN_HTML_PAGE_MANIFEST,
    load: () => import('./webHtmlPageBundle'),
  });
}
